import { useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  GetSearchCandidateProfileQueryQuery,
  SearchCandidateProfileMessageSender,
  useGetSearchCandidateProfileQueryQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { TableV3Filters } from 'common/table/components/TableV3';
import { useConversationCandidateCache } from 'modules/candidate-profile/hooks/useConversationCandidateCache';
import { useConversationRegeneration } from 'modules/candidate-profile/hooks/useOverwriteConversationMessages';
import { useSearchCandidateProfileQueryConversationHistory } from 'modules/candidate-profile/hooks/useSearchCandidateProfileQueryConversationHistory';
import { useSearchQueryPersistence } from 'modules/candidate-profile/hooks/useSearchQueryPersistence';
import { useVectorSearchAndReply } from 'modules/candidate-profile/hooks/useVectorSearchAndReply';
import { useAutoSendJobDetailsMessage } from 'modules/candidate-profile/hooks/useAutoSendJobDetailsMessage';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import {
  CandidateFilterGroups,
  CandidateFilterNode,
  CandidateFilterState
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { MergeAppliedFilters } from 'modules/candidate-profile/hooks/useCandidateFilterConditions';
import { extractStoredFilterTree } from 'modules/candidate-profile/tables/filters/extractStoredFilterTree';

type UseSearchVectorConversationProps = {
  candidateProfileListProps: CandidateProfileListProps;
  filters: TableV3Filters<CandidateProfileTableColumns> | undefined;
  filterConditions: CandidateFilterState[];
  filterGroups: CandidateFilterGroups;
  mergeAppliedFilters: MergeAppliedFilters;
  /** Replace the chips with a saved search's stored filter tree. */
  restoreSavedFilters: (tree: CandidateFilterNode) => void;
};

// Loaded, failed, or cleared from the URL ("New search"): nothing more will come.
const hasSavedSearchSettled = (
  searchQueryId: string | null,
  queryData: GetSearchCandidateProfileQueryQuery | undefined,
  queryError: ApolloError | undefined
) => !searchQueryId || queryData !== undefined || queryError !== undefined;

/**
 * Orchestrates the vector search conversation by composing multiple specialized hooks.
 * Provides a unified API for managing the conversation, search, and candidate data.
 */
export const useSearchVectorConversation = ({
  candidateProfileListProps,
  filters,
  filterConditions,
  filterGroups,
  mergeAppliedFilters,
  restoreSavedFilters
}: UseSearchVectorConversationProps) => {
  // URL persistence for search query ID
  const { searchQueryId, updateSearchQueryId, clearSearchQueryId } =
    useSearchQueryPersistence();

  // Fetch query data from backend
  const {
    data: queryData,
    loading: queryLoading,
    error: queryError
  } = useGetSearchCandidateProfileQueryQuery({
    variables: { id: searchQueryId || '' },
    skip: !searchQueryId,
    fetchPolicy: 'network-only'
  });

  // A search opened straight from its URL gets its chips from the stored
  // filter tree, as one picked from history does. The URL holds only the
  // column filters, not the ranges and radii the search ran. Until it loads,
  // the table does not know what to search for.
  const [awaitingSavedSearch, setAwaitingSavedSearch] = useState(
    Boolean(searchQueryId)
  );

  useEffect(() => {
    if (!awaitingSavedSearch) return;
    if (!hasSavedSearchSettled(searchQueryId, queryData, queryError)) return;
    setAwaitingSavedSearch(false);

    const tree = extractStoredFilterTree(queryData);
    if (tree) restoreSavedFilters(tree);
  }, [searchQueryId, queryData, queryError]);

  // Conversation history management
  const {
    conversationHistory,
    placeholderMessage,
    isInitialized,
    addMessages,
    setPlaceholder,
    resetHistory,
    regenerateFromIndex
  } = useSearchCandidateProfileQueryConversationHistory({
    searchQueryId,
    queryData,
    queryLoading,
    queryError
  });

  // Vector search and reply generation
  const {
    candidateProfiles,
    explanationContext,
    hasSearchResult,
    isSearching,
    isReplying,
    clearSearchCache,
    handlePlaceholderMessage,
    refetchVectorSearch
  } = useVectorSearchAndReply({
    candidateProfileListProps,
    filters,
    filterConditions,
    filterGroups,
    mergeAppliedFilters,
    searchQueryId,
    updateSearchQueryId,
    setPlaceholder,
    addMessages,
    awaitingSavedSearch
  });

  // Manage candidate cache for conversation links (without using search results)
  const { conversationCandidates, clearCache: clearConversationCache } =
    useConversationCandidateCache({
      conversationHistory
    });

  // Hook to handle conversation regeneration and message overwriting
  const { handleRegenerateFromMessage } = useConversationRegeneration({
    regenerateFromIndex
  });

  // Auto-send job details message when coming from analysis
  useAutoSendJobDetailsMessage({
    candidateProfileListProps,
    isVectorSearchInitialized: isInitialized,
    conversationHistoryLength: conversationHistory.length,
    handlePlaceholderMessage
  });

  const handleResetConversation = () => {
    // Prevent reset if a message is loading
    if (isSearching) {
      return;
    }
    resetHistory();
    clearConversationCache();
    clearSearchCache();
    clearSearchQueryId();
  };

  const handleSearch = (searchQuery: string, removeFields?: string[]) => {
    handlePlaceholderMessage(
      {
        id: `placeholder-${new Date().toISOString()}`,
        body: searchQuery,
        date: new Date().toISOString(),
        sender: SearchCandidateProfileMessageSender.User
      },
      removeFields
    );
  };

  return {
    candidateProfiles,
    explanationContext,
    isReplying,
    conversationCandidates,
    hasSearchResult,
    loading: isSearching,
    conversationHistory,
    placeholderMessage,
    isInitialized,
    isResetDisabled: isSearching,
    searchQueryId,
    handleSearch,
    handleResetConversation,
    handleRegenerateFromMessage,
    refetchVectorSearch
  };
};
